/**
 * Applicant Screening tab.
 *
 * Parity target: Angular `applicantTemplate` (config.component.html
 * lines 473–1033). Covers refund + lease-lead times, Credit Report
 * selector with Equifax XML/JSON conditional creds, Plaid env + creds,
 * Ninja required/version + V1/V2 endpoint secrets, plus the cornerstone-
 * only DB Backup section that POSTs `/api/tagged-backup`.
 *
 * Equifax CSS: `StaticCssFileBlock` mirrors `<app-savestylecss>` (GET/POST
 * `/api/config/static/equifax_report.css` and `equifax_config.css`).
 */

import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import {
  buildApplicantPayload,
  saveConfig,
  takeBackup,
} from '../../services/configService'
import type { ApplicantFormValues } from '../../types/config'
import { BACKUP_HOSTS } from '../../constants'
import { StaticCssFileBlock } from '../StaticCssFileBlock'
import {
  decimalTextInputAttrs,
  FieldLegend,
  integerTextInputAttrs,
  LabeledInput,
  LabeledTextArea,
  MaskedInput,
  SegmentedButtonGroup,
  } from './formPrimitives'

function shouldShowBackup(): boolean {
  if (typeof window === 'undefined') return false
  return BACKUP_HOSTS.includes(window.location.hostname)
}

import { SectionCard } from '@/components/ui/SectionCard'

import type { ApplicantTabProps } from '../../types'

const EMPTY: ApplicantFormValues = {
  REFUND_DELAY: '',
  DEFAULT_LEASE_LEAD_TIME: '',
  MAX_LEASE_LEAD_TIME: '',
  CREDIT_REPORT: '',
  CORELOGIC_AUTO_APPROVE: '',
  CORELOGIC_SCORE_APPROVE: '',
  CORELOGIC_SCORE_DENY: '',
  EQUIFAX_CUSTOMER_NUMBER: '',
  EQUIFAX_SECURITY_CODE: '',
  EQUIFAX_SITE_ID: '',
  EQUIFAX_SITE_PASSWORD: '',
  EQUIFAX_SERVICE_NAME: '',
  EQUIFAX_XML_API: '',
  EQUIFAX_CUSTOMER_NUMBER_JSON: '',
  EQUIFAX_SECURITY_CODE_JSON: '',
  EQUIFAX_JSON_CLIENT_ID: '',
  EQUIFAX_JSON_SECRET: '',
  EQUIFAX_JSON_API: '',
  PLAID_ENV: '',
  PLAID_CLIENT_ID_sandbox: '',
  PLAID_SECRET_sandbox: '',
  PLAID_CLIENT_ID_development: '',
  PLAID_SECRET_development: '',
  PLAID_CLIENT_ID_production: '',
  PLAID_SECRET_production: '',
  NINJA_REQUIRED: '',
  NINJA_VERSION: '',
  NINJA_JS_URL: '',
  NINJA_API_URL: '',
  NINJA_API_KEY: '',
  NINJA_JS_URL_V2: '',
  NINJA_API_URL_V2: '',
  NINJA_API_KEY_V2: '',
  NINJA_AUTH_API_URL: '',
}

export function ApplicantTab({ initialValues, isLoading, onSaved }: ApplicantTabProps) {
  const [submitLoading, setSubmitLoading] = useState(false)
  const [backupDescription, setBackupDescription] = useState('')
  const [backupLoading, setBackupLoading] = useState(false)
  const [snapshotMessage, setSnapshotMessage] = useState<string | null>(null)
  const showBackup = shouldShowBackup()
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty, isValid },
  } = useForm<ApplicantFormValues>({ defaultValues: EMPTY, mode: 'onChange' })

  useEffect(() => {
    if (initialValues) reset(initialValues)
  }, [initialValues, reset])

  const creditReport = watch('CREDIT_REPORT')
  const plaidEnv = watch('PLAID_ENV')
  const ninjaVersion = watch('NINJA_VERSION')

  const setField = (name: keyof ApplicantFormValues, value: string) =>
    setValue(name, value, { shouldDirty: true, shouldValidate: true })

  const onSubmit = async (values: ApplicantFormValues) => {
    try {
      setSubmitLoading(true)
      await saveConfig(buildApplicantPayload(values))
      showSuccess('Config Change Successfully!')
      reset(values)
      onSaved()
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleTakeBackup = async () => {
    setBackupLoading(true)
    try {
      const message = await takeBackup(backupDescription.trim())
      setSnapshotMessage(message || 'Snapshot created.')
    } catch (error) {
      showApiError(error)
    } finally {
      setBackupLoading(false)
    }
  }

  if (isLoading) {
    return <ModuleScreenLoading minHeight="300px" />
  }

  // Angular CSS counters only number visible `h2`s — DB Backup is host-gated.
  const n = (offset: number) => (showBackup ? offset : offset - 1)

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {showBackup && (
        <SectionCard number={1} title="DB Backup">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl items-end">
            <LabeledTextArea
              label="Description (Optional)"
              rows={2}
              value={backupDescription}
              onChange={(event) => setBackupDescription(event.target.value)}
            />
            <div>
              <button
                type="button"
                onClick={handleTakeBackup}
                disabled={backupLoading}
                className={primaryEntityActionButtonClass}
              >
                {backupLoading ? 'Working…' : 'Take a Snapshot'}
              </button>
            </div>
          </div>
        </SectionCard>
      )}

      <SectionCard number={n(2)} title="Refund & Lead Time">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
          <LabeledInput label="Refund Delay (Hours)" {...integerTextInputAttrs} {...register('REFUND_DELAY')} />
          <LabeledInput
            label="Default Lease Lead (Days)"
            {...integerTextInputAttrs}
            {...register('DEFAULT_LEASE_LEAD_TIME')}
          />
          <LabeledInput label="Max Lease Lead (Days)" {...integerTextInputAttrs} {...register('MAX_LEASE_LEAD_TIME')} />
        </div>
      </SectionCard>

      <SectionCard number={n(3)} title="Credit Report">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <FieldLegend label="Activate Credit Report">
            <SegmentedButtonGroup
              ariaLabel="Credit report provider"
              value={creditReport ?? ''}
              onChange={(v) => setField('CREDIT_REPORT', v)}
              options={[
                { value: 'ZipReport', label: 'ZipReport' },
                { value: 'CoreLogic', label: 'CoreLogic' },
                { value: 'Equifax', label: 'Equifax' },
              ]}
            />
          </FieldLegend>
          <FieldLegend label="CoreLogic Automation">
            <SegmentedButtonGroup
              ariaLabel="CoreLogic automation"
              value={watch('CORELOGIC_AUTO_APPROVE') ?? ''}
              onChange={(v) => setField('CORELOGIC_AUTO_APPROVE', v)}
              options={[
                { value: 'ON', label: 'Yes' },
                { value: 'OFF', label: 'No' },
              ]}
            />
          </FieldLegend>
          <div className="grid grid-cols-2 gap-2">
            <LabeledInput
              label="Approve ≥"
              {...decimalTextInputAttrs}
              {...register('CORELOGIC_SCORE_APPROVE')}
            />
            <LabeledInput
              label="Deny <"
              {...decimalTextInputAttrs}
              {...register('CORELOGIC_SCORE_DENY')}
            />
          </div>
        </div>

        {creditReport === 'Equifax' && (
          <div className="mt-4 space-y-4">
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">Equifax XML</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <LabeledInput label="Customer Number" {...register('EQUIFAX_CUSTOMER_NUMBER')} />
                <LabeledInput label="Security Code" {...register('EQUIFAX_SECURITY_CODE')} />
                <LabeledInput label="Site ID" {...register('EQUIFAX_SITE_ID')} />
                <LabeledInput label="Site Password" {...register('EQUIFAX_SITE_PASSWORD')} />
                <LabeledInput label="Service Name" {...register('EQUIFAX_SERVICE_NAME')} />
                <LabeledInput
                  label="XML API Endpoint"
                  className="sm:col-span-1"
                  {...register('EQUIFAX_XML_API')}
                />
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">Equifax JSON</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <LabeledInput
                  label="Customer Number"
                  {...register('EQUIFAX_CUSTOMER_NUMBER_JSON')}
                />
                <LabeledInput
                  label="Security Code"
                  {...register('EQUIFAX_SECURITY_CODE_JSON')}
                />
                <LabeledInput label="JSON Client ID" {...register('EQUIFAX_JSON_CLIENT_ID')} />
                <LabeledInput label="JSON Secret Key" {...register('EQUIFAX_JSON_SECRET')} />
                <LabeledInput
                  label="JSON API Endpoint"
                  className="sm:col-span-2"
                  {...register('EQUIFAX_JSON_API')}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
              <StaticCssFileBlock filename="equifax_report.css" />
              <StaticCssFileBlock filename="equifax_config.css" />
            </div>
          </div>
        )}
      </SectionCard>

      <SectionCard number={n(4)} title="Plaid">
        <FieldLegend label="Plaid Environment">
          <SegmentedButtonGroup
            ariaLabel="Plaid environment"
            value={plaidEnv ?? ''}
            onChange={(v) => setField('PLAID_ENV', v)}
            options={[
              { value: 'sandbox', label: 'Sandbox' },
              { value: 'development', label: 'Development' },
              { value: 'production', label: 'Production' },
            ]}
          />
        </FieldLegend>
        {(plaidEnv === '' || plaidEnv === 'sandbox') && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <LabeledInput label="Plaid Client ID (sandbox)" {...register('PLAID_CLIENT_ID_sandbox')} />
            <LabeledInput
              label="Plaid Secret Key (sandbox)"
              type="password"
              {...register('PLAID_SECRET_sandbox')}
            />
          </div>
        )}
        {plaidEnv === 'development' && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <LabeledInput
              label="Plaid Client ID (development)"
              {...register('PLAID_CLIENT_ID_development')}
            />
            <LabeledInput
              label="Plaid Secret Key (development)"
              type="password"
              {...register('PLAID_SECRET_development')}
            />
          </div>
        )}
        {plaidEnv === 'production' && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <LabeledInput
              label="Plaid Client ID (production)"
              {...register('PLAID_CLIENT_ID_production')}
            />
            <LabeledInput
              label="Plaid Secret Key (production)"
              type="password"
              {...register('PLAID_SECRET_production')}
            />
          </div>
        )}
      </SectionCard>

      <SectionCard number={n(5)} title="Ninja">
        <div className="flex flex-wrap gap-8">
          <FieldLegend label="Ninja Required">
            <SegmentedButtonGroup
              ariaLabel="Ninja required"
              value={watch('NINJA_REQUIRED') ?? ''}
              onChange={(v) => setField('NINJA_REQUIRED', v)}
              options={[
                { value: 'YES', label: 'YES' },
                { value: 'NO', label: 'NO' },
              ]}
            />
          </FieldLegend>
          <FieldLegend label="Ninja Version">
            <SegmentedButtonGroup
              ariaLabel="Ninja version"
              value={ninjaVersion ?? ''}
              onChange={(v) => setField('NINJA_VERSION', v)}
              options={[
                { value: 'V1', label: 'V1' },
                { value: 'V2', label: 'V2' },
              ]}
            />
          </FieldLegend>
        </div>

        {ninjaVersion === 'V1' && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <MaskedInput label="Ninja Js URL" {...register('NINJA_JS_URL')} />
            <MaskedInput label="Ninja API URL" {...register('NINJA_API_URL')} />
            <MaskedInput
              label="Ninja API Key"
              className="sm:col-span-2"
              {...register('NINJA_API_KEY')}
            />
          </div>
        )}
        {ninjaVersion === 'V2' && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <MaskedInput label="Ninja Js URL V2" {...register('NINJA_JS_URL_V2')} />
            <MaskedInput label="Ninja API URL V2" {...register('NINJA_API_URL_V2')} />
            <MaskedInput label="Ninja API Key V2" {...register('NINJA_API_KEY_V2')} />
            <MaskedInput
              label="Ninja Auth API URL"
              {...register('NINJA_AUTH_API_URL')}
            />
          </div>
        )}
      </SectionCard>

      <div>
        <button
          type="submit"
          disabled={!isDirty || submitLoading || !isValid}
          className={primaryEntityActionButtonClass}
        >
          {submitLoading ? 'Saving…' : 'Submit'}
        </button>
      </div>

      <ConfirmDialog
        isOpen={snapshotMessage !== null}
        title="DB Snapshot"
        message={snapshotMessage ?? ''}
        primaryLabel="Ok"
        onPrimary={() => setSnapshotMessage(null)}
        secondaryLabel=""
        onSecondary={() => setSnapshotMessage(null)}
        variant="info"
      />
    </form>
  )
}
